import { useTranslation } from '@chr/online-shared-ui-localizations';
import { Box, Typography } from '@mui/material';
import { PurchaseOrderItem } from '@/common/apis/dtos';
import { CustomGrid } from '@/common/components/grid/custom-grid';
import { HazMatSvgIcon, TempSvgIcon } from '@/common/components/icons/Icons';
import { GridColDef } from '@mui/x-data-grid-pro';
import { formatNumber, removeExtraSpace } from '@/common/utilities/display-utilities';
import ControlledTooltip from '@/common/components/controlled-tooltip/ControlledTooltip';

export interface PurchaseOrderItemsProps {
  items: PurchaseOrderItem[];
}

export const PurchaseOrderItems = ({ items }: PurchaseOrderItemsProps) => {
  const translate = useTranslation();

  const formattedItems = items.map(item => {
    return {
      poNumber: removeExtraSpace(item.purchaseOrderNumber, '-'),
      itemId: removeExtraSpace(item.itemId, '-'),
      sku: removeExtraSpace(item.sku, '-'),
      htCode: removeExtraSpace(item.htCode, '-'),
      destination: removeExtraSpace(item.destination, '-'),
      isHazmat: !!item.isHazMat,
      description: removeExtraSpace(item.description, '-'),
      isTempControlled: !!item.isTemperatureControlled,
      innerPackQty: (item.innerPackQty ?? 0) > 0 ? `${formatNumber(item.innerPackQty, 0, '-')} ${removeExtraSpace(item.packagingCode, '-')}` : '-',
    }
    
  })

  const columns: GridColDef[] = [
    {
      field: 'poNumber',
      headerName: translate('PO#'),
      flex: 1,
      width: 200
    },
    {
      field: 'itemId',
      headerName: translate('Item ID'),
      flex: 1,
    },
    {
      field: 'sku',
      headerName: translate('SKU'),
      flex: 1,
    },
    {
      field: 'description',
      headerName: translate('Description'),
      flex: 1,          
      renderCell: (item) => {
        return (
          <Box display="flex" alignItems={'center'} width={200}>
            {(item.row.isHazmat || item.row.isTempControlled) && (
              <Box display="flex" paddingRight="8px" gap="4px">
                {item.row.isHazmat && <HazMatSvgIcon />}
                {item.row.isTempControlled && <TempSvgIcon />}
              </Box>
            )}
            <ControlledTooltip title={item.row.description}>
              <Typography component="div" noWrap>
                {item.row.description}
              </Typography>
            </ControlledTooltip>                        
          </Box>
        );
      },
    },
    {
      field: 'htCode',
      headerName: translate('HT Code'),
      flex: 1,
    },
    {
      field: 'innerPackQty',
      headerName: translate('Inner Pack Qty'),
      flex: 1,      
      width: 150,
    },
    {
      field: 'destination',
      headerName: translate('Destination'),
      flex: 1,
      renderCell: (item) => {
        return (
          <ControlledTooltip title={item.row.destination}>
            <Typography component="div" noWrap>
              {item.row.destination}
            </Typography>
          </ControlledTooltip>
        );
      }
    },
  ];

  return (
    <CustomGrid
        dataTestId="po-cargo-items-table"
        columns={columns}
        items={formattedItems}
        sortModel={[
            {
                field: 'poNumber',
                sort: 'desc',
            },
        ]}
    />
  );
};
